import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useT, localizedPath } from "../i18n";
import Logo from "./Logo";

const anchor = (id) => `#${id}`;

export default function Header() {
  const { t, lang } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // language switch keeps same subpath, no scroll
  const switchLang = (target) => {
    if (target === lang) return;
    // Map current path to new lang, keep sub route
    const parts = location.pathname.split("/").filter(Boolean); // e.g. ['ar','privacy-policy']
    parts[0] = target;
    const next = "/" + parts.join("/");
    navigate(next, { replace: false, state: { langSwitch: true } });
  };

  const isHome = /^\/(ar|en)\/?$/.test(location.pathname);

  const goAnchor = (id) => (e) => {
    if (isHome) {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) {
        if (window.__lenis) {
          window.__lenis.scrollTo(el, { offset: -80 });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
      setOpen(false);
    }
    // if not home, let the Link navigate to /ar or /en with #hash
  };

  const links = [
    { key: "projects", id: "projects", label: t("nav.projects") },
    { key: "howItWorks", id: "how-it-works", label: t("nav.howItWorks") },
    { key: "principles", id: "principles", label: t("nav.about") },
    { key: "contact", id: "contact", label: t("nav.contact") },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || open ? "glass" : ""
      }`}
      style={{ borderBottom: scrolled ? "1px solid rgba(26,33,28,0.06)" : "1px solid transparent" }}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 h-[72px] flex items-center justify-between">
        <Link
          to={localizedPath(lang)}
          onClick={goAnchor("top")}
          className="no-tap-highlight"
          data-testid="header-brand-link"
          aria-label="Mirexilora"
        >
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.key}
              href={`${localizedPath(lang)}${anchor(l.id)}`}
              onClick={goAnchor(l.id)}
              className="link-underline text-[0.88rem] font-medium text-[#1A211C]/85 hover:text-[#1A211C]"
              data-testid={`header-link-${l.key}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="hidden sm:flex items-center rounded-full border border-[#1a211c]/15 overflow-hidden text-[0.76rem] font-mono tracking-widest"
            role="group"
            aria-label={t("nav.language")}
            data-testid="lang-switcher"
          >
            <button
              type="button"
              onClick={() => switchLang("ar")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "ar" ? "bg-[#1a211c] text-[#f6f4ee]" : "text-[#1a211c]/70 hover:text-[#1a211c]"
              }`}
              data-testid="lang-switch-ar"
              aria-pressed={lang === "ar"}
            >
              AR
            </button>
            <button
              type="button"
              onClick={() => switchLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en" ? "bg-[#1a211c] text-[#f6f4ee]" : "text-[#1a211c]/70 hover:text-[#1a211c]"
              }`}
              data-testid="lang-switch-en"
              aria-pressed={lang === "en"}
            >
              EN
            </button>
          </div>

          <a
            href={`${localizedPath(lang)}#contact`}
            onClick={goAnchor("contact")}
            className="hidden md:inline-flex btn-olive"
            data-testid="header-cta-start"
          >
            {t("cta.startNow")}
            <ArrowUpRight size={16} />
          </a>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#1a211c]/15 text-[#1a211c] no-tap-highlight"
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.65, 0, 0.35, 1] }}
            className="lg:hidden border-t border-[#1a211c]/10 bg-[#f6f4ee]"
            data-testid="mobile-menu-panel"
          >
            <div className="max-w-[1440px] mx-auto px-5 sm:px-8 py-8">
              <ul className="flex flex-col gap-5">
                {links.map((l) => (
                  <li key={l.key}>
                    <a
                      href={`${localizedPath(lang)}${anchor(l.id)}`}
                      onClick={goAnchor(l.id)}
                      className="font-serif text-3xl tracking-tight text-[#1a211c]"
                      data-testid={`mobile-link-${l.key}`}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => switchLang("ar")}
                  className={`px-4 py-2 rounded-full text-xs font-mono tracking-widest border ${
                    lang === "ar"
                      ? "bg-[#1a211c] text-[#f6f4ee] border-[#1a211c]"
                      : "border-[#1a211c]/20 text-[#1a211c]/75"
                  }`}
                  data-testid="mobile-lang-ar"
                >
                  AR
                </button>
                <button
                  type="button"
                  onClick={() => switchLang("en")}
                  className={`px-4 py-2 rounded-full text-xs font-mono tracking-widest border ${
                    lang === "en"
                      ? "bg-[#1a211c] text-[#f6f4ee] border-[#1a211c]"
                      : "border-[#1a211c]/20 text-[#1a211c]/75"
                  }`}
                  data-testid="mobile-lang-en"
                >
                  EN
                </button>
                <a
                  href={`${localizedPath(lang)}#contact`}
                  onClick={goAnchor("contact")}
                  className="btn-olive ml-auto"
                  data-testid="mobile-cta-start"
                >
                  {t("cta.startNow")}
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
